import type { ReactNode } from "react";
import { useLocation } from "react-router";
import { Button, Card } from "../ds";
import { api, type AuthUser } from "../lib/api";
import { displayName, signOut } from "../lib/hooks/useAuth";

// Die beiden Ansichten vor der App-Shell (docs/auth.md): ohne Session der
// Login, mit Session aber ohne Rolle `access` die Erklärung. Beide liegen hier
// zusammen — sie teilen Rahmen und Zweck, und getrennte Dateien wären zwei
// Dateien für zwanzig Zeilen.

/** Pflichtangaben unter der Anmeldung — dieselben Ziele und dieselbe Stelle wie
 *  in appeulektro (`(login)/component/footer.tsx`), damit beide Anmeldeseiten
 *  gleich aussehen. `.ds-auth-footer` bringt das Design-System schon mit. */
function AuthFooter() {
  return (
    <footer className="ds-auth-footer">
      <span>&copy; {new Date().getFullYear()} Eulektro GmbH</span>
      {/* Neuer Tab: ein Klick soll die halb ausgefüllte Anmeldung nicht wegwerfen. */}
      <a href="https://www.eulektro.de/impressum" target="_blank" rel="noreferrer">
        Impressum
      </a>
      <a href="https://www.eulektro.de/datenschutz" target="_blank" rel="noreferrer">
        Datenschutz
      </a>
    </footer>
  );
}

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="ds-auth-shell">
      <div className="ds-auth-main">
        <div className="ds-auth-card">
          <div className="ds-auth-logo">
            <img src="/spyship-logo.svg" alt="Eulektro Spyship" />
          </div>
          {children}
        </div>
      </div>
      <AuthFooter />
    </div>
  );
}

/**
 * Einstieg in den Keycloak-Code-Flow. Der Knopf navigiert echt (`location.href`)
 * — ein `fetch` würde am 302 zu Keycloak scheitern.
 */
export function LoginPage() {
  const location = useLocation();
  const next = location.pathname + location.search;

  return (
    <AuthShell>
      <Card title="Anmeldung erforderlich">
        <p className="text-sm leading-relaxed text-content-2">
          Nutze deinen Eulektro-Account um dich im Spyship anzumelden.
        </p>
        <div className="ds-auth-actions">
          <Button
            variant="primary"
            iconName="lock"
            onClick={() => {
              window.location.href = api.loginUrl(next);
            }}
          >
            Mit Eulektro-Account anmelden
          </Button>
        </div>
      </Card>
    </AuthShell>
  );
}

/**
 * Angemeldet, aber ohne Client-Rolle `access`. Hier NICHT zum Login schicken:
 * der Nutzer ist bereits angemeldet, ein Redirect ergäbe eine Endlosschleife.
 */
export function NoAccessPage({ user }: { user: AuthUser | null }) {
  return (
    <AuthShell>
      <Card title="Kein Zugriff">
        <p className="text-sm leading-relaxed text-content-2">
          {user ? (
            <>
              Angemeldet als <strong className="text-content">{displayName(user)}</strong>. Diesem
              Konto fehlt die Berechtigung für die Router-Verwaltung.
            </>
          ) : (
            <>Diesem Konto fehlt die Berechtigung für die Router-Verwaltung.</>
          )}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-content-2">
          Wende dich an die IT, um die Rolle freischalten zu lassen. Mit einem anderen Konto
          anmelden geht über Abmelden.
        </p>
        <div className="ds-auth-actions">
          <Button
            variant="outline"
            iconName="logout"
            onClick={() => {
              void signOut();
            }}
          >
            Abmelden
          </Button>
        </div>
      </Card>
    </AuthShell>
  );
}
